<script lang="ts" setup>
import { ref } from 'vue';
const props = defineProps<{
    modelValue: number | string | boolean | undefined | 'Yes' | 'No',
    min: number | undefined,
    max: number | undefined,
    options: string[] | string | undefined,
    id: string | undefined,
    checkGuid: string | undefined
}>();
const val = ref<Date | undefined>(undefined);
const emit = defineEmits(["update", "next"]);

// Capture is persisted to IndexedDB asynchronously, which can lose a write if the app is
// backgrounded/killed right after a timestamp is hit (mid-cycle checks are often left for
// long real-world gaps). Mirror the captured time to localStorage - a synchronous write
// that commits immediately - so a lost async write can still be recovered instead of
// silently re-stamping "now" later.
const backupKey = () => `cm_timestamp_${props.checkGuid}_${props.id}`;
const readBackup = (): Date | undefined => {
    if (!props.checkGuid || !props.id) return undefined;
    try {
        const raw = localStorage.getItem(backupKey());
        return raw ? new Date(raw) : undefined;
    } catch {
        return undefined;
    }
}
const writeBackup = (d: Date) => {
    if (!props.checkGuid || !props.id) return;
    try {
        localStorage.setItem(backupKey(), d.toISOString());
    } catch {
        // storage unavailable/full - nothing more we can do
    }
}
const clearBackup = () => {
    if (!props.checkGuid || !props.id) return;
    try {
        localStorage.removeItem(backupKey());
    } catch {
        // ignore
    }
}

const load = (value: string | number | boolean | undefined) => {
    console.log(`[TS-DEBUG] TimestampComponent.load id=${props.id} received value=`, value, `current val.value=`, val.value);
    if (value) {
        // Already has a recorded time (e.g. re-synced during final submit) - keep it, don't re-navigate.
        val.value = new Date(value as string);
        clearBackup();
        console.log(`[TS-DEBUG] TimestampComponent.load id=${props.id} PRESERVED existing ->`, val.value);
        return;
    }
    if (val.value == undefined) {
        const fromBackup = readBackup();
        val.value = fromBackup ?? new Date();
        writeBackup(val.value);
        console.log(`[TS-DEBUG] TimestampComponent.load id=${props.id} FIRST HIT (fromBackup=${!!fromBackup}) ->`, val.value);
        emit("update");
        emit("next");
    }
}
const validate = () :  'under' | 'over' | 'invalid' | 'ok' | 'ignore' | 'error' => {
    return 'ok'
}
// The saved time from the bound task value. load() isn't called when the user lands on this
// page via "Previous" (e.g. after resuming a check at a later step), so value() must fall back
// to this rather than re-stamping "now" over the recorded time.
const savedValue = (): Date | undefined => {
    if (!props.modelValue || typeof props.modelValue === 'boolean') return undefined;
    const d = new Date(props.modelValue as string);
    return isNaN(d.getTime()) ? undefined : d;
}
const value = () => {
   if (val.value == undefined) {
       const saved = savedValue();
       if (saved) return val.value = saved;
       val.value = readBackup() ?? new Date();
       writeBackup(val.value);
   }
   return val.value;
}
defineExpose({ validate, load, value });
</script>

<template>
    <div class="message">
        {{props.options}}
    </div>
</template>

<style scoped>

</style>